import { Filetype } from "idfweb-vfs";

import type { MessageLevel, Reporter, Task } from "idfweb-tasks";
import type { FsClient } from "idfweb-runtime-protocol/fs-rpc";

/** A file or folder dragged in from the host, read from the drop's `DataTransfer`. */
export interface Dropped {
    name: string;
    /** A file's contents; a folder has `children` instead. */
    file?: File;
    children?: Dropped[];
}

/**
 * The files and folders of a drop. The entries are taken from `data` before
 * the first await, since a `DataTransfer` is empty once its event's handler
 * returns; the folders are read after that.
 */
export function readDrop(data: DataTransfer): Promise<Dropped[]> {
    const reads: Promise<Dropped | undefined>[] = [];
    for (const item of Array.from(data.items)) {
        if (item.kind !== "file") continue;
        const entry = item.webkitGetAsEntry();
        const file = item.getAsFile();
        if (entry !== null) reads.push(readEntry(entry));
        else if (file !== null) reads.push(Promise.resolve({ name: file.name, file }));
    }
    return Promise.all(reads).then((items) => items.filter((item): item is Dropped => item !== undefined));
}

async function readEntry(entry: FileSystemEntry): Promise<Dropped | undefined> {
    if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
        return { name: entry.name, file };
    }
    if (!entry.isDirectory) return undefined;
    // readEntries returns the entries in batches, and an empty one at the end.
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const entries: FileSystemEntry[] = [];
    for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
        if (batch.length === 0) break;
        entries.push(...batch);
    }
    const children = await Promise.all(entries.map(readEntry));
    return { name: entry.name, children: children.filter((child): child is Dropped => child !== undefined) };
}

export const REPLACE = "Replace";
const CANCEL = "Cancel";

/** How long a copy runs before it gets a progress toast. */
const QUIET_MS = 500;

export interface CopyContext {
    fs: Pick<FsClient, "stat" | "mkdir" | "writeFile" | "delete">;
    ask: (level: MessageLevel, text: string, actions: readonly string[]) => Promise<string | undefined>;
    reporter: Reporter;
}

/**
 * Copy dropped files and folders into the guest folder `dir`. When names are
 * taken there, it asks once whether to replace them all, as VS Code does; a
 * replaced folder is deleted first, not merged. Resolves to the paths it
 * wrote at the top, or to undefined if the question was declined.
 */
export async function copyDropped(context: CopyContext, dir: string, items: Dropped[]): Promise<string[] | undefined> {
    const { fs, ask, reporter } = context;
    const taken: Dropped[] = [];
    for (const item of items) {
        if (
            await fs.stat(`${dir}/${item.name}`).then(
                () => true,
                () => false,
            )
        )
            taken.push(item);
    }
    if (taken.length > 0 && (await ask("warn", takenText(dir, taken), [REPLACE, CANCEL])) !== REPLACE) return undefined;

    let files = 0;
    let bytes = 0;
    const count = (item: Dropped): void => {
        if (item.file !== undefined) {
            files++;
            bytes += item.file.size;
        }
        item.children?.forEach(count);
    };
    items.forEach(count);

    let task: Task | undefined;
    let written = 0;
    const where = dir.slice(dir.lastIndexOf("/") + 1);
    const slow = setTimeout(() => {
        task = reporter.task(`Copying ${files} ${files === 1 ? "file" : "files"} into ${where}`, {
            total: bytes,
            unit: "bytes",
        });
        task.progress(written);
    }, QUIET_MS);
    const copy = async (item: Dropped, path: string): Promise<void> => {
        if (item.file !== undefined) {
            task?.progress(written, { detail: path });
            const data = new Uint8Array(await item.file.arrayBuffer());
            await fs.writeFile(path, data, { create: true, overwrite: true });
            written += item.file.size;
            task?.progress(written);
            return;
        }
        await fs.mkdir(path);
        for (const child of item.children ?? []) await copy(child, `${path}/${child.name}`);
    };
    try {
        const paths: string[] = [];
        for (const item of items) {
            const path = `${dir}/${item.name}`;
            if (taken.includes(item)) {
                const stat = await fs.stat(path);
                const both = stat.filetype !== Filetype.DIRECTORY && item.file !== undefined;
                if (!both) await fs.delete(path, true);
            }
            await copy(item, path);
            paths.push(path);
        }
        clearTimeout(slow);
        task?.done();
        return paths;
    } catch (e) {
        clearTimeout(slow);
        const why = e instanceof Error ? e.message : String(e);
        if (task !== undefined) task.failed(why);
        else reporter.message("error", `Could not copy into ${where}: ${why}`);
        return undefined;
    }
}

function takenText(dir: string, taken: Dropped[]): string {
    const where = dir.slice(dir.lastIndexOf("/") + 1);
    if (taken.length === 1) {
        return `A file or folder named ${taken[0]!.name} is already in ${where}. Do you want to replace it?`;
    }
    const names = taken.map((item) => item.name);
    const shown = names.length > 3 ? [...names.slice(0, 3), `${names.length - 3} more`] : names;
    return `${shown.join(", ")} are already in ${where}. Do you want to replace them?`;
}
